
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { AppSetting } from "@/types/settings";
import { useToast } from "@/hooks/use-toast";

interface AppSettingsTableProps {
  settings: AppSetting[];
  isLoading: boolean;
  onSaveSetting: (setting: AppSetting) => Promise<void>;
}

const AppSettingsTable = ({ settings, isLoading, onSaveSetting }: AppSettingsTableProps) => {
  const { toast } = useToast();
  const [localSettings, setLocalSettings] = useState<AppSetting[]>(settings);
  const [editingSettings, setEditingSettings] = useState<Record<string, boolean>>({});

  // Update local settings when props change
  useEffect(() => {
    if (settings.length > 0) {
      setLocalSettings(settings);
    }
  }, [settings]);

  // Filter out email template settings and feature flags as they're handled by separate components
  const filteredSettings = localSettings.filter(setting => 
    !setting.key.includes('EMAIL_TEMPLATE') && setting.category !== 'Feature Flags'
  );

  const handleValueChange = (setting: AppSetting, value: string) => {
    const updatedSetting = { ...setting, value };
    setLocalSettings(prevSettings => 
      prevSettings.map(s => s.id === setting.id ? updatedSetting : s)
    );
    setEditingSettings(prev => ({ ...prev, [setting.id]: true }));
  };

  const handleSaveSetting = async (setting: AppSetting) => {
    try {
      await onSaveSetting(setting);
      setEditingSettings(prev => ({ ...prev, [setting.id]: false }));
      toast({
        title: "Setting saved",
        description: `${setting.key} has been updated successfully.`,
      });
    } catch (error) {
      console.error("Error saving setting:", error);
      toast({
        title: "Error",
        description: "Failed to save setting. Please try again.",
        variant: "destructive",
      });
    }
  };

  const isLongContent = (value: string) => {
    return value.length > 100 || value.includes('\n');
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-5 py-10 text-center shadow-sm">
        <div className="animate-pulse text-gray-500">Loading settings...</div>
      </div>
    );
  }

  if (filteredSettings.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-5 py-10 text-center text-muted-foreground shadow-sm">
        No general settings found.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
        <h3 className="text-sm font-semibold text-gray-900">General settings</h3>
        <p className="mt-1 text-sm text-gray-500">
          Inline editing is kept visible and compact so common configuration changes do not require drilling into a separate form.
        </p>
      </div>

      <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-gray-200 hover:bg-transparent">
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Setting</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Value</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Category</TableHead>
            <TableHead className="bg-white text-right text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Save</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredSettings.map((setting) => (
            <TableRow key={setting.id} className={editingSettings[setting.id] ? "bg-whatsapp-light/40" : "hover:bg-gray-50"}>
              <TableCell>
                <div>
                  <div className="font-medium text-gray-900">{setting.key}</div>
                  <div className="text-sm text-muted-foreground">{setting.description}</div>
                </div>
              </TableCell>
              <TableCell className="max-w-md">
                {isLongContent(setting.value) ? (
                  <Textarea
                    value={setting.value}
                    onChange={(e) => handleValueChange(setting, e.target.value)}
                    className="min-h-[120px] w-full resize-y rounded-lg border-gray-200 bg-white"
                    placeholder="Enter content..."
                  />
                ) : (
                  <Input
                    value={setting.value}
                    onChange={(e) => handleValueChange(setting, e.target.value)}
                    className="h-10 rounded-lg border-gray-200 bg-white"
                  />
                )}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="border-gray-200 bg-gray-100 text-gray-600">
                  {setting.category}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  onClick={() => handleSaveSetting(setting)}
                  size="sm"
                    disabled={!editingSettings[setting.id]}
                  variant={editingSettings[setting.id] ? "default" : "outline"}
                  className={
                    editingSettings[setting.id]
                      ? "rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark"
                      : "rounded-lg border-gray-200 text-gray-500"
                  }
                >
                  {editingSettings[setting.id] ? "Save" : "Saved"}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
};

export default AppSettingsTable;
